import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Wordmark } from './Brand';
import { Button } from './ui/button';
import { useAuth } from '@/lib/auth';
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, User as UserIcon } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';

const NAV = [
  { to: '/about', label: 'About' },
  { to: '/schools', label: 'Schools' },
  { to: '/programs', label: 'Programs' },
  { to: '/admissions', label: 'Admissions' },
  { to: '/student-services', label: 'Student Services' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = user && user.roles && (user.roles.includes('super_admin') || user.roles.includes('registrar') || user.roles.includes('billing') || user.roles.includes('support'));

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B1F3A]/90 border-b border-white/10" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          <Link to="/" data-testid="home-link"><Wordmark light /></Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to} to={n.to}
                data-testid={`nav-${n.to.replace('/', '')}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium transition-colors text-white hover:text-gold ${
                    isActive ? 'after:absolute after:left-4 after:right-4 after:-bottom-0.5 after:h-[2px] after:bg-gold after:rounded-full' : ''
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {!user && (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-gold">Log in</Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="btn-gold">Enroll Now</Button>
                </Link>
              </>
            )}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 text-white hover:text-gold transition-colors px-3 py-2 rounded-md">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover ring-2 ring-gold/40" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gold/20 text-gold flex items-center justify-center text-sm font-semibold">
                        {user.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
                  <DropdownMenuLabel className="font-display">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Student Dashboard
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <UserIcon className="h-4 w-4 mr-2" /> Admin Console
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <button
            className="lg:hidden text-white p-2" onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0B1F3A] px-4 py-3" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2.5 text-sm transition-colors text-white hover:text-gold ${
                  isActive ? 'border-l-2 border-gold pl-2.5' : ''
                }`
              }>
              {n.label}
            </NavLink>
          ))}
          <div className="border-t border-white/10 mt-3 pt-3 space-y-2">
            {!user ? (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2 text-white">Log in</Link>
                <Link to="/programs" onClick={() => setOpen(false)} className="block px-3 py-2 text-gold font-semibold">Enroll Now</Link>
              </>
            ) : (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="block px-3 py-2 text-white">Dashboard</Link>
                {isAdmin && <Link to="/admin" onClick={() => setOpen(false)} className="block px-3 py-2 text-white">Admin</Link>}
                <button onClick={() => { logout(); setOpen(false); navigate('/'); }} className="block px-3 py-2 text-white/80">Log out</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
